import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLang } from "../context/LanguageContext";
import { RevealLine, RichLine, ScrubWords, ImageReveal } from "./Reveal";
import { EssenceMarquee } from "./Marquee";

const TRIPTYCH_IMAGES = [
  "https://images.unsplash.com/photo-1767680148632-137c54313c35?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  "https://images.unsplash.com/photo-1685537711146-d6c29e41eeb6?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  "https://images.unsplash.com/photo-1781344334903-f33d8b76e292?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
];

export const Statement = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y0 = useTransform(scrollYProgress, [0, 1], ["4%", "-6%"]);
  const y1 = useTransform(scrollYProgress, [0, 1], ["14%", "-16%"]);
  const y2 = useTransform(scrollYProgress, [0, 1], ["7%", "-10%"]);
  const ys = [y0, y1, y2];

  return (
    <section data-testid="statement-section" className="relative overflow-hidden">
      <EssenceMarquee />

      <div className="max-w-5xl mx-auto px-5 sm:px-8 pt-12 pb-20 text-center">
        <h2 className="font-display text-4xl sm:text-6xl lg:text-7xl font-medium leading-[1.06] tracking-tight text-neutral-900">
          {t.statement.lines.map((line, i) => (
            <RevealLine key={i} delay={0.1 + i * 0.15}>
              <RichLine segments={line} />
            </RevealLine>
          ))}
        </h2>
        <ScrubWords
          text={t.statement.body}
          className="mt-10 max-w-2xl mx-auto text-base sm:text-lg text-neutral-800 leading-relaxed text-center"
        />
      </div>

      <div ref={ref} className="max-w-7xl mx-auto px-5 sm:px-8 pb-28 grid grid-cols-1 sm:grid-cols-3 gap-10 sm:gap-8 items-start">
        {t.triptych.map((c, i) => (
          <motion.figure key={i} style={{ y: ys[i] }} className={i === 1 ? "sm:mt-24" : i === 2 ? "sm:mt-10" : ""}>
            <ImageReveal src={TRIPTYCH_IMAGES[i]} alt={c.label} className="aspect-[3/4] rounded-lg" />
            <figcaption className="mt-5 font-display italic text-2xl sm:text-3xl text-neutral-900">
              {c.label}
            </figcaption>
          </motion.figure>
        ))}
      </div>
    </section>
  );
};

export const BigQuote = () => {
  const { t } = useLang();
  return (
    <section data-testid="quote-section" className="py-24 sm:py-36 bg-white border-y border-[#e8e5e0]">
      <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center">
        <span className="font-display text-8xl leading-none text-[#dcd8d0] select-none">"</span>
        <ScrubWords
          text={t.bigQuote.text}
          className="font-display italic text-2xl sm:text-3xl lg:text-4xl leading-snug text-neutral-900 -mt-6 text-center"
        />
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4, duration: 0.8 }}
          className="mt-8 font-tech text-[11px] tracking-[0.4em] uppercase text-neutral-400"
        >
          — {t.bigQuote.author}
        </motion.p>
      </div>
    </section>
  );
};

export const CTA = () => {
  const { t } = useLang();
  const goBooking = () => {
    const el = document.getElementById("booking");
    if (window.lenis && el) window.lenis.scrollTo(el, { offset: -70 });
    else el?.scrollIntoView({ behavior: "smooth" });
  };
  return (
    <section data-testid="cta-section" className="relative py-32 sm:py-48 text-center px-5 overflow-hidden bg-neutral-950">
      <video
        poster="/videos/embers.jpg"
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover opacity-90"
      >
        <source src="/videos/embers.mp4" type="video/mp4" />
        <source src="/videos/embers.webm" type="video/webm" />
      </video>
      <div className="absolute inset-0 bg-black/65" />
      <div className="relative z-10">
        <motion.img
          src="/logos/emberline-final-512.png"
          alt="Emberline Chimney Co."
          initial={{ opacity: 0, scale: 0.85 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          className="w-24 h-24 sm:w-32 sm:h-32 mx-auto mb-8 object-contain drop-shadow-[0_0_30px_rgba(255,107,0,0.35)]"
        />
        <RevealLine>
          <span className="font-tech text-[11px] tracking-[0.4em] uppercase text-white/50">{t.cta.kicker}</span>
        </RevealLine>
        <h2 className="mt-6 font-display text-5xl sm:text-6xl lg:text-8xl leading-[1.05] tracking-tight text-white">
          {t.cta.lines.map((line, i) => (
            <RevealLine key={i} delay={0.12 + i * 0.15}>
              <RichLine segments={line} />
            </RevealLine>
          ))}
        </h2>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <button
            data-testid="cta-book-btn"
            onClick={goBooking}
            className="btn-fire btn-slide mt-12 rounded-full px-10 py-5 font-tech text-sm font-semibold tracking-widest uppercase text-white"
          >
            <span className="label-wrap"><span>{t.cta.button}</span><span aria-hidden="true">{t.cta.button}</span></span>
          </button>
        </motion.div>
      </div>
    </section>
  );
};
